"use client";
import { useEffect, useMemo, useState } from "react";
import { Search, Plus, Trash2 } from "lucide-react";
import Navbar from "components/Navbar/Index";
import { Spinner } from "components";
import { getCustomerPhoneNumberDetails } from "service/apis/CustomerRegistrationapi";
import { Calcgst, createGenericOrder, CreateGenericOrderRequestType, createLeadContact, CreateLeadContactReqType, getProductsGeneric } from "service/apis/Opportunities";
import { useNavigate } from "react-router-dom";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, AlertDialogPortal, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import ExistingContactDetail from "pages/Opportunities/Sheet/step1";
import { toast } from "react-toastify";
import { AxiosRequestConfig } from "axios";
import { useAppSelector, useAppDispatch } from "redux/hooks";
import { resetState } from "redux/features/CreateOpportunity";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { ChevronDown, Check } from "lucide-react";
import { useSelectState } from "pages/Opportunities/utils/useSelect";
import { getRelationShipManager } from "service/apis/PartnerRegistrationapi";


/* ------------------ TYPES ------------------ */
type Customer = {
    id: number;
    full_name: string;
    phone_number: string;
    email: string;
};


type OrderRow = {
    productId: number | null;
    qty: number | "";
    selling_price: number | "";
    cost_price: number | "";
    gst: number | "";
    otherCharges: number | "";
    total: number;
    gstManuallyEdited?: boolean;
    priceError?: boolean; // ✅ added
};



type Product = {
    id: number;
    name: string;
    subCategory: string;
};


export default function CreateGenericOrderPage() {
    const navigate = useNavigate();
    /* ------------------ STATE ------------------ */
    const [loading, setLoading] = useState(false);
    const [openAddContact, setOpenAddContact] = useState(false);
    const [openProductIndex, setOpenProductIndex] = useState<number | null>(null);
    const [prefillPhone, setPrefillPhone] = useState<string>("");
    const [customerSearch, setCustomerSearch] = useState("");
    const [products, setProducts] = useState<Product[]>([]);
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

    const [rows, setRows] = useState<OrderRow[]>([
        {
            productId: null,
            qty: "",
            selling_price: "",
            cost_price: "",
            gst: "",
            otherCharges: "",
            total: 0,
        },
    ]);

    /* ------------------ CUSTOMER SEARCH ------------------ */
    useEffect(() => {
        if (!customerSearch.trim()) {
            setCustomers([]);
            return;
        }

        const timer = setTimeout(async () => {
            try {
                setLoading(true);

                const response = await getCustomerPhoneNumberDetails({
                    params: {
                        number: customerSearch,
                    },
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                });
                const raw = Array.isArray(response.data)
                    ? response.data
                    : [response.data];

                const mappedCustomers: Customer[] = raw.map((item: any) => ({
                    id: item.id,
                    full_name: item.full_name,
                    phone_number: item.phone_number,
                    email: item.email,
                }));

                setCustomers(mappedCustomers);



                setCustomers(mappedCustomers);
            } catch (error) {
                console.error("Customer search failed", error);
                setCustomers([]);
            } finally {
                setLoading(false);
            }
        }, 400);

        return () => clearTimeout(timer);
    }, [customerSearch]);


    // calc gst 
    const calculateGSTForRow = async (
        index: number,
        amount: number
    ) => {
        try {
            const config: AxiosRequestConfig = {
                data: { amount },
            };

            const res = await Calcgst(config);

            const gstValue = res.data?.gst ?? 0;

            setRows((prev) =>
                prev.map((row, i) =>
                    i === index
                        ? {
                            ...row,
                            gst: gstValue,
                            gstManuallyEdited: false,
                        }
                        : row
                )
            );
        } catch (err) {
            console.error("GST calculation failed", err);
        }
    };

    // product search 
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                setLoading(true);

                const response = await getProductsGeneric({
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                });

                const mappedProducts: Product[] =
                    response.data?.map((p: any) => ({
                        id: p.id,
                        name: p.name,
                        subCategory: p.subCategory || "Others",
                    })) || [];

                setProducts(mappedProducts);
            } catch (error) {
                console.error("Failed to fetch products", error);
            } finally {
                setLoading(false);
            }
        };

        fetchProducts();
    }, []);
    const groupedProducts = useMemo<Record<string, Product[]>>(() => {
        if (!Array.isArray(products)) return {};

        return products.reduce((acc, product) => {
            const key = product.subCategory || "Others";
            if (!acc[key]) acc[key] = [];
            acc[key].push(product);
            return acc;
        }, {} as Record<string, Product[]>);
    }, [products]);

    /* ------------------ ROW CALCULATION ------------------ */
    const updateRow = (
        index: number,
        key: keyof OrderRow,
        value: number | string
    ) => {
        setRows((prev) =>
            prev.map((row, i) => {
                if (i !== index) return row;

                const updated = {
                    ...row,
                    [key]: value,
                };

                const qty = Number(updated.qty || 0);
                const selling = Number(updated.selling_price || 0);
                const cost = Number(updated.cost_price || 0);
                const gst = Number(updated.gst || 0);
                const other = Number(updated.otherCharges || 0);

                return {
                    ...updated,
                    total: qty * selling + gst + other,
                    priceError: cost > 0 && selling < cost, // ✅ validation flag
                };
            })
        );

        if (key === "qty" || key === "selling_price") {
            const row = rows[index];
            const qty = Number(key === "qty" ? value : row.qty || 0);
            const amount = Number(key === "selling_price" ? value : row.selling_price || 0);

            if (qty > 0 && amount > 0) {
                calculateGSTForRow(index, qty * amount);
            }
        }
    };




    const addRow = () => {
        setRows((prev) => [
            ...prev,
            {
                productId: "",
                qty: "",
                selling_price: "",
                cost_price: "",
                gst: "",
                otherCharges: "",
                total: "",
            },
        ]);
    };

    const removeRow = (index: number) => {
        if (rows.length > 1) {
            setRows((prev) => prev.filter((_, i) => i !== index));
        }
    };

    /* ------------------ TOTALS ------------------ */
    const calculation = useMemo(() => {
        const subtotal = rows.reduce(
            (sum, r) => sum + Number(r.qty || 0) * Number(r.selling_price || 0),
            0
        );

        const gst = rows.reduce(
            (sum, r) => sum + Number(r.gst || 0),
            0
        );

        const other = rows.reduce(
            (sum, r) => sum + Number(r.otherCharges || 0),
            0
        );

        const grandTotal = subtotal + gst + other;

        return {
            subtotal,
            gst,
            other,
            grandTotal,
        };
    }, [rows]);





    // handlers

    const handleCreateContact = async () => {
        setLoading(true);

        const auth = localStorage.getItem("authToken");
        if (!auth) {
            toast.error("Authentication token not found");
            setLoading(false);
            return null;
        }

        const authToken = JSON.parse(auth);

        const payload: CreateLeadContactReqType = {
            contactName: contact_person_name,
            contactNumber: contact_person_phone.length>=10 ? contact_person_phone : prefillPhone,
            contactEmail: customer_contact_email,
            contactAddress: company_address ? [company_address] : [],
            contactPersonType: "individual",
            notes: contact_note,
        };

        const config: AxiosRequestConfig = {
            headers: { Authorization: `Bearer ${authToken}` },
            data: payload,
        };

        try {
            const res = await createLeadContact(config);

            toast.success("Contact created successfully");

            // Auto-select newly created contact
            setSelectedCustomer({
                id: res.data.id,
                full_name: contact_person_name,
                phone_number: contact_person_phone,
                email: customer_contact_email,
            });

            setCustomerSearch(contact_person_name);
            setOpenAddContact(false);
            setCustomers([]);

            dispatch(resetState());
            return res.data.id;
        } catch (err: any) {
            toast.error(
                err.response?.data?.message || "Failed to create contact"
            );
            return null;
        } finally {
            setLoading(false);
        }
    };

    const handleCustomerSelect = (customer: Customer) => {
        setSelectedCustomer(customer);
        setCustomerSearch(customer.full_name);
        setCustomers([]);
    };



    /* ------------------ SUBMIT ------------------ */

    const handleSubmit = async () => {
        if (calculation.grandTotal <= 0) {
            toast.error("Grand total must be greater than zero");
            setLoading(false);
            return;
        }
        if (!selectedCustomer) {
            alert("Please select customer");
            return;
        }

        setLoading(true);

        try {
            const payload: CreateGenericOrderRequestType = {
                customers_id: selectedCustomer.id,
                amout_before_taxes: calculation.subtotal,
                gst: calculation.gst,
                other_charge: calculation.other,
                total_payable: calculation.grandTotal,
                assigned_to: assignedToOption?.value ?? null, owned_by: ownedByOption?.value ?? null,
                product_rows: rows.map((row) => ({
                    product_id: row.productId as number,
                    quantity: Number(row.qty || 0),
                    inr_amount: Number(row.selling_price || 0),
                    cost_price: Number(row.cost_price || 0),
                    profit: (Number(row.selling_price || 0) - Number(row.cost_price || 0)) * Number(row.qty || 0),
                    gst: Number(row.gst || 0),
                    other_charge: Number(row.otherCharges || 0),

                })),
            };

            const token = localStorage.getItem("token") || "";

            const response = await createGenericOrder(payload, token);
            navigate("/genericOrder");
        } catch (error) {
            console.error("Create order failed", error);
            alert("Failed to create order");
        } finally {
            setLoading(false);
        }
    };

    const getProductName = (productId: number | null) => {
        return products.find(p => p.id === productId)?.name || "-";
    };
    const {
        contact_person_name,
        contact_person_phone,
        customer_contact_email,
        company_address,
        contact_note,
    } = useAppSelector((state) => state.CreateOpportunity);

    const dispatch = useAppDispatch();
    const selectedProductIds = rows
        .map((r) => r.productId)
        .filter(Boolean) as number[];


    const isCustomerValid = !!selectedCustomer;

    const isRowsValid = useMemo(() => {
        return rows.every((row) => {
            return (
                row.productId !== null &&
                Number(row.qty) > 0 &&
                Number(row.selling_price) > 0 &&
                Number(row.cost_price) >= 0 &&
                Number(row.selling_price) >= Number(row.cost_price) && // ✅ rule
                Number(row.gst) >= 0
            );
        });
    }, [rows]);

    // validation
    const isContactValid =
        contact_person_name &&
        (prefillPhone &&
            prefillPhone.length >= 10 || contact_person_phone && contact_person_phone.length >= 10);


    const getRowTotal = (row: OrderRow) => {
        const qty = Number(row.qty || 0);
        const amount = Number(row.selling_price || 0);
        const gst = Number(row.gst || 0);
        const other = Number(row.otherCharges || 0); // can be negative

        return qty * amount + gst + other;
    };



    const {
        options: relationShipManagerOptions,
        selectedOption: assignedToOption,
        handleChange: handleAssignedToChange,
    } = useSelectState(
        [],
        null,
        () => { },
        () => { },
        fetchRelationShipManager as () => Promise<Option[]>,
    );

    const {
        options: ownerOptions,
        selectedOption: ownedByOption,
        handleChange: handleOwnedByChange,
    } = useSelectState(
        [],
        null,
        () => { },
        () => { },
        fetchRelationShipManager as () => Promise<Option[]>,
    );

    async function fetchRelationShipManager(): Promise<void | Option[]> {
        // get auth token from localstorage
        const auth: string | null = localStorage.getItem("authToken");
        if (auth === null) return;
        const authToken = JSON.parse(auth);

        const req = {
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
        };
        const name = localStorage.getItem("name");
        const is_owner = localStorage.getItem("is_owner");
        const owner_name = localStorage.getItem("owner_name");
        const myself = localStorage.getItem("staff_id");

        try {
            const res = await getRelationShipManager(req);

            const options = res.data.map((obj) => ({
                label: obj?.fname,
                value: obj?.id,
            }));

            const index = options.findIndex((item) => item?.value == myself);
            if (index > 0) {
                const item = options.splice(index, 1)[0];
                options.unshift(item);
            }

            return options as Option[];
        } catch (error) {
            toast.error("Error fetching options staff");
        }
    }


    const isFormValid = calculation.grandTotal >= 0 && isCustomerValid && isRowsValid && assignedToOption !== null && ownedByOption !== null;
    console.log("valid form", calculation.grandTotal, isCustomerValid, isRowsValid, assignedToOption, ownedByOption);
    /* ------------------ UI ------------------ */
    return (
        < >
            {loading && <Spinner />}

            {/* Header */}
            <div className="m-4">
                <Navbar children="Orders" />
                <AlertDialog>
                    <AlertDialogTrigger>
                        <button className="rounded-lg absolute right-6 top-6 py-2 px-4 border bg-gray-button-gray-lite border-gray-border-gray-lite">
                            Cancel Order
                        </button>
                    </AlertDialogTrigger>
                    <AlertDialogPortal>
                        <AlertDialogOverlay className="bg-black-900 bg-opacity-60 animate-overlayShow fixed inset-0" />
                        <AlertDialogContent className="bg-white-A700 rounded-lg p-0 max-w-2xl animate-contentShow">
                            <AlertDialogHeader className="flex text-left gap-4 pt-8 pb-4 px-10">
                                <AlertDialogTitle className="font-bold text-xl leading-7 tracking-[2%] text-black-label">
                                    Are you absolutely sure?
                                </AlertDialogTitle>
                                <AlertDialogDescription className="font-medium text-base leading-6 tracking-[2%] text-[#7E7E7E]">
                                    This action can not be undone. This will permanently delete your progress and remove the actions preformed.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter className="flex justify-end items-center gap-2.5 py-5 px-10 flex-row rounded-b-lg bg-gray-button-gray-lite">
                                <AlertDialogCancel
                                    className="m-0 rounded-lg border-blue-700 border py-[11px] px-4 text-blue-700"
                                    asChild
                                >
                                    <button>Close</button>
                                </AlertDialogCancel>
                                <AlertDialogAction
                                    className="rounded-lg border-blue-700 py-[11px] px-4 bg-blue-700 border text-white-A700"
                                    asChild
                                >
                                    <button onClick={() => navigate("/genericOrder")} >Continue</button>
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialogPortal>
                </AlertDialog>
            </div>
            <div className="  m-4 rounded-xl bg-white-A700">
                <div className="flex p-3 sm:flex-col flex-row gap-4 justify-between">
                    {/* LEFT PANEL */}
                    <div className="flex-1 bg-white rounded-lg shadow-sm border p-6">

                        {/* CUSTOMER SECTION */}
                        <div className="mb-6">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Customer *
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search and select customer..."
                                    value={customerSearch}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/[^0-9]/g, "");
                                        setCustomerSearch(value);
                                        setSelectedCustomer(null);
                                    }}
                                    className="w-full px-4 py-2.5 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                                />

                                <Search className="absolute right-3 top-3 h-5 w-5 text-gray-400" />

                                {customerSearch.trim() && !selectedCustomer && (
                                    <div className="absolute z-10 bg-white-A700 border border-gray-200 w-full mt-1 rounded-lg shadow-lg max-h-56 overflow-y-auto">

                                        {/* Existing customers */}
                                        {customers.map((c) => (
                                            <div
                                                key={c.id}
                                                className="p-3 hover:bg-blue-50 cursor-pointer transition border-b last:border-b-0"
                                                onClick={() => handleCustomerSelect(c)}
                                            >
                                                <div className="flex sm:flex-col flex-row items-center gap-2">
                                                    <p className="text-gray-900 whitespace-nowrap">{c.full_name}</p>
                                                    <p className="text-gray-900">{c.phone_number}</p>
                                                </div>
                                            </div>
                                        ))}

                                        {/* Add Contact CTA */}
                                        {customerSearch.trim() &&
                                            customers.length === 0 &&
                                            !loading &&
                                            !selectedCustomer && (
                                                <div
                                                    onClick={() => {
                                                        setPrefillPhone(customerSearch);
                                                        setOpenAddContact(true);
                                                    }}

                                                    className="p-3 text-blue-700 font-medium cursor-pointer hover:bg-blue-50 flex items-center justify-center gap-2"
                                                >
                                                    + Add Contact
                                                </div>
                                            )}


                                    </div>
                                )}
                                <Dialog open={openAddContact} onOpenChange={setOpenAddContact}>
                                    <DialogContent className="max-w-lg max-h-[90vh] bg-white-A700 rounded-xl overflow-y-auto">
                                        <DialogHeader>
                                            <DialogTitle>Add New Contact</DialogTitle>
                                        </DialogHeader>

                                        <ExistingContactDetail
                                            errors={{}}
                                            prefilledPhone={prefillPhone}
                                        />

                                        <button
                                            type="button"
                                            disabled={!isContactValid}
                                            onClick={handleCreateContact}
                                            className="px-4 py-[11px] rounded-lg text-white-A700 bg-blue-700 disabled:opacity-40"
                                        >
                                            Save & Close
                                        </button>

                                    </DialogContent>
                                </Dialog>

                            </div>
                            {selectedCustomer && (
                                <div className="mt-2 text-sm text-green-600 font-medium">
                                    ✓ Selected: {selectedCustomer.full_name}
                                </div>
                            )}
                        </div>
                        <div className="flex flex-wrap flex-row items-end gap-2">
                            <div><label>Created by</label> <input
                                type="text"
                                value={localStorage.getItem("name") || ""}
                                disabled
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm"
                            /></div>


                            <div className="flex flex-col"><label>Owned by</label>    <Popover>
                                <PopoverTrigger asChild>
                                    <button
                                        type="button"
                                        className="
        w-[260px]
        h-10
        flex
        items-center
        justify-between
        rounded-md
        border
        border-gray-300
        bg-white
        px-3
        text-sm
        text-gray-700
        focus:outline-none
        focus:ring-2
        focus:ring-blue-500
      "
                                    >
                                        <span
                                            className={
                                                ownedByOption
                                                    ? "text-gray-900"
                                                    : "text-gray-400"
                                            }
                                        >
                                            {ownedByOption?.label || "Select staff"}
                                        </span>

                                        <ChevronDown className="h-4 w-4 text-gray-500" />
                                    </button>
                                </PopoverTrigger>

                                <PopoverContent className="p-0 w-[260px]" align="start">
                                    <Command>
                                        <CommandInput placeholder="Search staff..." />
                                        <CommandList>
                                            <CommandEmpty>No staff found.</CommandEmpty>

                                            <CommandGroup heading="Staff">
                                                {relationShipManagerOptions.map((staff) => (
                                                    <CommandItem
                                                        key={staff.value}
                                                        onSelect={() => handleOwnedByChange(staff)}
                                                        className="flex items-center justify-between"
                                                    >
                                                        <span>{staff.label}</span>

                                                        {ownedByOption?.value === staff.value && (
                                                            <Check className="h-4 w-4 text-blue-600" />
                                                        )}
                                                    </CommandItem>
                                                ))}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover></div>
                            <div className="flex flex-col  gap-2">
                                <span>Assigned to:</span>

                                <Popover>
                                    <PopoverTrigger asChild>
                                        <button
                                            type="button"
                                            className="
        w-[260px]
        h-10
        flex
        items-center
        justify-between
        rounded-md
        border
        border-gray-300
        bg-white
        px-3
        text-sm
        text-gray-700
        focus:outline-none
        focus:ring-2
        focus:ring-blue-500
      "
                                        >
                                            <span
                                                className={
                                                    assignedToOption
                                                        ? "text-gray-900"
                                                        : "text-gray-400"
                                                }
                                            >
                                                {assignedToOption?.label || "Select staff"}
                                            </span>

                                            <ChevronDown className="h-4 w-4 text-gray-500" />
                                        </button>
                                    </PopoverTrigger>

                                    <PopoverContent className="p-0 w-[260px]" align="start">
                                        <Command>
                                            <CommandInput placeholder="Search staff..." />
                                            <CommandList>
                                                <CommandEmpty>No staff found.</CommandEmpty>

                                                <CommandGroup heading="Staff">
                                                    {relationShipManagerOptions.map((staff) => (
                                                        <CommandItem
                                                            key={staff.value}
                                                            onSelect={() => handleAssignedToChange(staff)}
                                                            className="flex items-center justify-between"
                                                        >
                                                            <span>{staff.label}</span>

                                                            {assignedToOption?.value === staff.value && (
                                                                <Check className="h-4 w-4 text-blue-600" />
                                                            )}
                                                        </CommandItem>
                                                    ))}
                                                </CommandGroup>
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>

                            </div>
                        </div>
                        {/* PRODUCTS TABLE */}
                        <div className="mb-6">
                            <div className="flex items-center justify-between mb-4">
                                <label className="text-sm font-semibold text-gray-700">
                                    Order Items
                                </label>
                                <button
                                    type="button"
                                    onClick={addRow}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm text-white-A700 font-medium"
                                >
                                    <Plus className="h-4 w-4" />
                                    Add Item
                                </button>
                            </div>

                            {/* Table Header */}
                            <div className="grid grid-cols-12 sm:hidden  gap-3 mb-2 text-xs font-semibold text-gray-600 uppercase">
                                <div className="col-span-2">Product</div>
                                <div className="col-span-1">Qty</div>
                                <div className="col-span-2">Selling Price (₹)</div>
                                <div className="col-span-2">Cost Price (₹)</div>
                                <div className="col-span-1">GST (₹)</div>
                                <div className="col-span-1">Other (₹)</div>
                                <div className="col-span-2">Total (₹)</div>
                                <div className="col-span-1"></div>
                            </div>

                            {/* Table Rows */}
                            <div className="space-y-3">
                                {rows.map((row, index) => (
                                    <div
                                        key={index}
                                        className="grid grid-cols-12 gap-3 sm:flex sm:flex-col sm:space-y-4"
                                    >

                                        <div className="col-span-2">
                                            <div className="col-span-3 sm:flex sm:items-center sm:gap-3">
                                                <p className="hidden sm:block w-24 text-sm font-medium">Product</p>

                                                <Popover>
                                                    <PopoverTrigger asChild>
                                                        <button
                                                            type="button"
                                                            className="w-full  sm:flex-1 flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                                                        >
                                                            <span className="truncate">
                                                                {row.productId
                                                                    ? products.find(p => p.id === row.productId)?.name
                                                                    : "Select product"}
                                                            </span>
                                                            <ChevronDown className="h-4 w-4 text-gray-500" />
                                                        </button>
                                                    </PopoverTrigger>

                                                    <PopoverContent className="p-0 sm:w-5/6 w-[280px]" align="start">
                                                        <Command>
                                                            <CommandInput placeholder="Search product..." />

                                                            <CommandList>
                                                                <CommandEmpty>No products found.</CommandEmpty>

                                                                {Object.entries(groupedProducts ?? {}).map(([subCategory, items]) => (
                                                                    <CommandGroup key={subCategory} heading={subCategory}>
                                                                        {(items ?? []).map((p) => {
                                                                            const isAlreadySelected =
                                                                                selectedProductIds.includes(p.id) &&
                                                                                rows[index].productId !== p.id;

                                                                            return (
                                                                                <CommandItem
                                                                                    key={p.id}
                                                                                    disabled={isAlreadySelected}
                                                                                    className={`text-black-900 ${isAlreadySelected
                                                                                        ? "opacity-40 cursor-not-allowed"
                                                                                        : "cursor-pointer"
                                                                                        }`}
                                                                                    onSelect={() => {
                                                                                        if (isAlreadySelected) return;

                                                                                        updateRow(index, "productId", p.id);
                                                                                        setOpenProductIndex(null);
                                                                                    }}
                                                                                >
                                                                                    {p.name}
                                                                                    {isAlreadySelected && (
                                                                                        <span className="ml-2 text-xs text-gray-500">
                                                                                            (Already selected)
                                                                                        </span>
                                                                                    )}
                                                                                </CommandItem>
                                                                            );
                                                                        })}
                                                                    </CommandGroup>
                                                                ))}

                                                            </CommandList>
                                                        </Command>

                                                    </PopoverContent>
                                                </Popover>
                                            </div>


                                        </div>
                                        <div className="col-span-1 ">

                                            <div className="col-span-1 sm:flex sm:items-center sm:gap-3">
                                                <p className="hidden sm:block w-24 text-sm font-medium">Qty</p>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    placeholder="0"
                                                    value={row.qty}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        updateRow(index, "qty", value === "" ? "" : Number(value));
                                                    }}
                                                    className="w-full sm:flex-1 px-3 py-2 border rounded-lg text-sm"
                                                />
                                            </div>

                                        </div>

                                        <div className="col-span-2">
                                            <div className="col-span-2  sm:flex sm:items-center sm:gap-3">
                                                <p className="hidden sm:block w-24 text-sm font-medium">Selling Price</p>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    placeholder="0"

                                                    value={row.selling_price}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        updateRow(index, "selling_price", value === "" ? "" : Number(value));
                                                    }}
                                                    className="w-full sm:flex-1 px-3 py-2 border rounded-lg text-sm"
                                                />
                                                {row.priceError && (
                                                    <p className="text-xs text-red-600 mt-1">
                                                        Selling price cannot be less than cost price
                                                    </p>
                                                )}

                                            </div>
                                        </div>
                                        <div className="col-span-2">
                                            <div className="col-span-2 sm:flex sm:items-center sm:gap-3">
                                                <p className="hidden sm:block w-24 text-sm font-medium">Cost Price</p>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    placeholder="0"
                                                    value={row.cost_price}

                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        updateRow(index, "cost_price", value === "" ? "" : Number(value));
                                                    }}
                                                    className="w-full sm:flex-1 px-3 py-2 border rounded-lg text-sm"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-span-1">
                                            <div className="col-span-2 sm:flex sm:items-center sm:gap-3">
                                                <p className="hidden sm:block w-24 text-sm font-medium">GST</p>
                                                <input
                                                    type="number"
                                                    placeholder="0"
                                                    step="0.01"
                                                    value={row.gst}

                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        setRows((prev) =>
                                                            prev.map((r, i) =>
                                                                i === index
                                                                    ? {
                                                                        ...r,
                                                                        gst: value === "" ? "" : Number(value),
                                                                        gstManuallyEdited: true,
                                                                    }
                                                                    : r
                                                            )
                                                        );
                                                    }}
                                                    className="w-full sm:flex-1 px-3 py-2 border rounded-lg text-sm"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-span-1">
                                            <div className="col-span-2 sm:gap-4 sm:flex sm:flex-row sm:items-center">
                                                <p className="hidden sm:block w-24 text-sm font-medium">Other<br></br> Charges</p>
                                                <input
                                                    type="number"
                                                    placeholder="0"
                                                    step="0.01"
                                                    value={row.otherCharges}

                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        updateRow(index, "otherCharges", value === "" ? "" : Number(value));
                                                    }}

                                                    className="w-full sm:flex-1 px-3 py-2 border rounded-lg text-sm"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-span-2">
                                            <div className="col-span-1 sm:gap-4 sm:flex sm:flex-row sm:items-center">
                                                <p className="hidden sm:block">Total</p>
                                                <input
                                                    type="text"
                                                    value={getRowTotal(row).toFixed(2)}
                                                    disabled
                                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-span-1">
                                            <button
                                                type="button"
                                                onClick={() => removeRow(index)}
                                                disabled={rows.length === 1}
                                                className="w-full p-2 text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-30 disabled:cursor-not-allowed"
                                            >
                                                <Trash2 className="h-4 w-4 sm:w-6 sm:h-6 mx-auto" />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* SUBMIT BUTTON */}
                        <div className="flex justify-end pt-4 border-t">
                            <button
                                onClick={handleSubmit}
                                disabled={loading || !isFormValid}
                                className={`px-6 py-3 rounded-lg font-semibold transition
    ${loading || !isFormValid
                                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                        : "bg-blue-600 text-white-A700 hover:bg-blue-700"
                                    }
  `}
                            >
                                {loading ? "Processing..." : "Create Order"}
                            </button>

                        </div>
                    </div>

                    {/* RIGHT PANEL - CALCULATION */}
                    <div className="w-80 bg-white rounded-lg shadow-sm border p-6 h-fit sticky top-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-4 pb-3 border-b">
                            Order Summary
                        </h3>

                        {/* PRODUCT BREAKDOWN */}
                        <div className="space-y-3 mb-4">
                            {rows.map((row, index) => (
                                <div key={index} className="border rounded-lg p-3 text-sm">
                                    <div className="font-semibold text-gray-900">
                                        {getProductName(row.productId)}
                                    </div>

                                    <div className="flex justify-between text-gray-600 mt-1">
                                        <span>Qty × Selling price</span>
                                        <span>
                                            {row.qty || 0} × ₹{Number(row.selling_price || 0).toFixed(2)}
                                        </span>

                                    </div>

                                    <div className="flex justify-between text-gray-600">
                                        <span>GST</span>
                                        <span>₹{Number(row.gst || 0).toFixed(2)}</span>

                                    </div>

                                    <div className="flex justify-between text-gray-600">
                                        <span>Other</span>
                                        <span>₹{Number(row.otherCharges || 0).toFixed(2)}</span>
                                    </div>

                                    <div className="flex justify-between font-semibold mt-1">
                                        <span>Total</span>
                                        <span>₹{getRowTotal(row).toFixed(2)}</span>

                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* GRAND TOTAL */}
                        <div className="space-y-2 border-t pt-3">
                            <div className="flex justify-between text-sm">
                                <span>Order amount</span>
                                <span>₹{calculation.subtotal.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span>GST</span>
                                <span>₹{calculation.gst.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span>Other</span>
                                <span>₹{calculation.other.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-lg font-bold text-blue-600">
                                <span>Grand Total</span>
                                <span>₹{calculation.grandTotal.toFixed(2)}</span>
                            </div>
                            {calculation.grandTotal < 0 && (
                                <p className="text-sm text-red-600 font-medium">
                                    Grand total cannot be negative
                                </p>
                            )}

                        </div>
                    </div>

                </div>
            </div>
        </>
    );
}
